import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Play, 
  Square, 
  RotateCcw, 
  Trash2, 
  FolderOpen, 
  Globe, 
  Gamepad,
  Users,
  HardDrive,
  Cpu,
  Copy,
  Check,
  AlertCircle,
  Clock3,
  Wifi,
  WifiOff,
  Terminal,
  ScrollText,
  RefreshCw
} from 'lucide-react';
import { serverApi } from '../services/api';
import { SecureFileManager } from '../components/SecureFileManager';
import { LogsModal } from '../components/LogsModal';
import { MinecraftConsoleModal } from '../components/MinecraftConsoleModal';
import { StartupProgress } from '../components/StartupProgress';
import { ActivityLog } from '../components/ActivityLog';
import { BackupsPanel } from '../components/BackupsPanel';
import { useSocket } from '../contexts/SocketContext';
import { useTranslation } from 'react-i18next';
import type {
  AppliedServerSpec,
  OwnerServerOperation,
  ServerAutoShutdownState,
  ServerLifecycleOperation,
  ServerMonitoringSnapshot,
  ServerProvisioningState,
} from '../types/server';
import { formatBytes, formatNanoCpus } from '../utils/resourceFormatting';
import { isMonitoringStale } from '../utils/monitoring';
import { getApiErrorDetails, type ApiErrorDetails } from '../utils/apiError';
import { formatSettingLabel, formatSettingValue, isLifecycleOperationActive } from '../utils/serverUi';

interface ServerDetails {
  _id: string;
  name: string;
  displayName: string;
  game: string;
  status: 'creating' | 'starting' | 'running' | 'stopping' | 'stopped' | 'error' | 'suspended';
  actualState?: string;
  desiredState?: string;
  subdomain: string;
  port: number;
  containerName: string;
  containerId?: string;
  stateReason?: string;
  provisioning?: ServerProvisioningState;
  lifecycleOperation?: ServerLifecycleOperation;
  ownerOperation?: OwnerServerOperation | null;
  backupLock?: { backupId: string; phase: string; startedAt: string };
  packageId?: {
    name: string;
    displayName: string;
    ram: number;
    cpu?: number;
    storage?: number;
    maxPlayers: number;
    price: number;
  } | null;
  appliedSpec?: AppliedServerSpec;
  capabilities?: {
    minecraftConsole?: boolean;
  };
  gameSettings: Record<string, unknown>;
  startupProgress?: {
    phase: string;
    percent: number;
    details?: string;
    startedAt: string;
    completedAt?: string;
  };
  monitoring: ServerMonitoringSnapshot;
  autoShutdown?: ServerAutoShutdownState;
  agentStatus: {
    isConnected: boolean;
    lastHealthCheck: string;
    systemMetrics: {
      containers: number;
      ram: string;
      cpu: string;
    };
    name: string;
    region: string;
  };
  realTimeStats: {
    canConnect: boolean;
    serverAddress: string | null;
    directAddress?: string | null;
    dnsAddress?: string | null;
    estimatedPing: string;
  };
  dnsRecords?: Array<{ type: string; name: string; content: string }>;
  sftp: {
    available: boolean;
    host: string;
    port: number;
    username: string;
    activeConnections: number;
    lastStatusCheck: string;
    connectionString: string;
  };
  createdAt: string;
}

export function ServerDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [server, setServer] = useState<ServerDetails | null>(null);
  const serverRef = useRef<ServerDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [actionError, setActionError] = useState<ApiErrorDetails | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showFileManager, setShowFileManager] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [showMinecraftConsole, setShowMinecraftConsole] = useState(false);
  const deleteRequestedRef = useRef(false);
  const { socket, connected: wsConnected } = useSocket();
  const { t, i18n } = useTranslation();

  const fetchServerDetails = useCallback(async () => {
    try {
      const response = await serverApi.getServer(id!);
      if (response.success) {
        serverRef.current = response.server;
        setServer(response.server);
        deleteRequestedRef.current = response.server?.desiredState === 'deleted';
        setLoadError('');
        setRefreshError('');
      } else {
        throw new Error(t('serverDetails.loadFailed'));
      }
    } catch (err: any) {
      const details = getApiErrorDetails(err, t('serverDetails.loadFailed'));
      if (details.status === 404 && deleteRequestedRef.current) {
        navigate('/dashboard', { replace: true });
        return;
      }
      if (serverRef.current) setRefreshError(details.message);
      else setLoadError(details.message);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    fetchServerDetails();
    // REST remains the recovery source after refresh or a missed Socket.IO event.
    const interval = setInterval(fetchServerDetails, 5000);
    return () => clearInterval(interval);
  }, [fetchServerDetails]);

  // Real-time metrics updates via WebSocket
  useEffect(() => {
    if (!socket || !id) return;

    const handleMetricsUpdate = (data: any) => {
      if (data.serverId !== id) return;
      
      console.log('[WebSocket] Received real-time metrics for server', id);
      
      setServer(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          status: data.status || prev.status,
          actualState: data.actualState || prev.actualState,
          desiredState: data.desiredState || prev.desiredState,
          monitoring: data.monitoring || prev.monitoring,
          startupProgress: data.startupProgress ?? prev.startupProgress,
          autoShutdown: data.autoShutdown ?? prev.autoShutdown,
          ownerOperation: Object.prototype.hasOwnProperty.call(data, 'ownerOperation')
            ? data.ownerOperation
            : prev.ownerOperation,
        };
      });
      setRefreshError('');
    };

    socket.on('server:metrics', handleMetricsUpdate);
    
    return () => {
      socket.off('server:metrics', handleMetricsUpdate);
    };
  }, [socket, id]);

  const handleAction = async (action: string) => {
    if (!server) return;
    if (action === 'delete' && !window.confirm(t('serverDetails.deleteConfirm', { name: server.displayName }))) {
      return;
    }
    setActionLoading(action);
    setActionError(null);
    try {
      const response = action === 'delete'
        ? await serverApi.deleteServer(server._id)
        : action === 'start'
          ? await serverApi.startServer(server._id)
          : action === 'stop'
            ? await serverApi.stopServer(server._id)
            : await serverApi.restartServer(server._id);
      if (!response?.success) throw new Error(t('serverDetails.actionFailed', { action }));
      if (action === 'delete') deleteRequestedRef.current = true;
      setServer(previous => previous ? {
        ...previous,
        desiredState: response.result?.desiredState ?? previous.desiredState,
        actualState: response.result?.actualState ?? previous.actualState,
        ownerOperation: Object.prototype.hasOwnProperty.call(response.result || {}, 'ownerOperation')
          ? response.result.ownerOperation
          : previous.ownerOperation,
        stateReason: t('serverDetails.actionAccepted', { action }),
      } : previous);
      await fetchServerDetails();
    } catch (err: any) {
      setActionError(getApiErrorDetails(err, t('serverDetails.actionFailed', { action })));
    } finally {
      setActionLoading(null);
    }
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'running': return 'badge-online';
      case 'stopped': return 'badge-offline';
      case 'creating':
      case 'starting':
      case 'stopping':
      case 'restarting':
      case 'deleting': return 'badge-warning';
      case 'error':
      case 'suspended': return 'badge-error';
      default: return 'badge-offline';
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-dark-700 rounded w-1/4" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="h-64 bg-dark-700 rounded-xl" />
              <div className="h-48 bg-dark-700 rounded-xl" />
            </div>
            <div className="space-y-6">
              <div className="h-32 bg-dark-700 rounded-xl" />
              <div className="h-48 bg-dark-700 rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadError || !server) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="empty-state">
          <AlertCircle className="empty-state-icon text-red-400" />
          <h2 className="empty-state-title">{t('serverDetails.notFound')}</h2>
          <p className="empty-state-text">{loadError || t('serverDetails.notFoundDesc')}</p>
          <button onClick={() => fetchServerDetails()} className="btn-secondary">
            <RefreshCw className="w-4 h-4" />
            {t('serverDetails.retryLoad')}
          </button>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            {t('serverDetails.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  const lifecycleOperationActive = isLifecycleOperationActive(server.lifecycleOperation);
  const ownerOperationActive = server.ownerOperation?.status === 'in_progress';
  const ownerOperationFailed = server.ownerOperation?.status === 'failed';
  const ownerPhaseLabel = server.ownerOperation
    ? t(`serverOperation.phases.${server.ownerOperation.phase}`)
    : null;
  const ownerErrorLabel = server.ownerOperation?.error
    ? t(`serverOperation.errors.${server.ownerOperation.error.code}`)
    : null;
  const operationActive = lifecycleOperationActive || ownerOperationActive;
  const backupOperationActive = Boolean(server.backupLock?.backupId);
  const operationType = ownerOperationActive
    ? ownerPhaseLabel
    : (lifecycleOperationActive ? server.lifecycleOperation?.type : undefined);
  const canStart = !operationActive && !backupOperationActive && (
    server.actualState === 'stopped'
    || (server.actualState === 'error' && Boolean(server.containerId))
  );
  const canStop = !operationActive && !backupOperationActive && server.actualState === 'running';
  const canRestart = !operationActive && !backupOperationActive && server.actualState === 'running';
  const displayStatus = server.desiredState === 'deleted'
    ? 'deleting'
    : (operationType === 'restart' ? 'restarting' : server.status);
  const appliedSpec = server.appliedSpec;
  const appliedPackageName = appliedSpec?.package.displayName
    || server.packageId?.displayName
    || t('serverDetails.legacyPackage');
  const appliedMemory = appliedSpec
    ? formatBytes(appliedSpec.resources.memoryBytes)
    : (server.packageId?.ram !== undefined ? `${server.packageId.ram} GB` : '—');
  const appliedCpu = appliedSpec
    ? formatNanoCpus(appliedSpec.resources.nanoCpus)
    : (server.packageId?.cpu !== undefined ? `${server.packageId.cpu} vCPU` : '—');
  const appliedStorage = appliedSpec
    ? formatBytes(appliedSpec.resources.storageBytes)
    : (server.packageId?.storage !== undefined ? `${server.packageId.storage} GB` : '—');
  const appliedMaxPlayers = appliedSpec?.limits.maxPlayers
    ?? server.packageId?.maxPlayers
    ?? server.monitoring.playerStats.maxPlayers;
  const monitoringStale = isMonitoringStale(server.monitoring);
  const scheduledTimestamp = server.autoShutdown?.scheduledFor
    ? new Date(server.autoShutdown.scheduledFor).getTime()
    : Number.NaN;
  const autoShutdownScheduledFor = server.actualState === 'running'
    && !monitoringStale
    && server.monitoring.playerStats.success
    && Number.isFinite(scheduledTimestamp)
      ? new Date(scheduledTimestamp)
      : null;
  const healthOnline = server.monitoring.health.online && !monitoringStale;
  const liveResources = monitoringStale ? null : server.monitoring.resources;
  const publicSettings = Object.entries(server.gameSettings || {});
  const retryState = server.lifecycleOperation?.retryable
    ? server.lifecycleOperation
    : (server.provisioning?.retryable ? server.provisioning : undefined);
  const typedActionErrorCode = actionError?.code === 'preparing_server_data'
    ? 'server_data_not_ready'
    : actionError?.code;
  const actionErrorMessage = typedActionErrorCode
    && ['no_start_placement_available', 'server_data_not_ready', 'generic'].includes(typedActionErrorCode)
      ? t(`serverOperation.errors.${typedActionErrorCode}`)
      : actionError?.message;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 text-gray-400 hover:text-gray-200 hover:bg-dark-700 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 flex items-center gap-4">
          <div className="w-12 h-12 bg-neon-500/10 rounded-xl flex items-center justify-center">
            <Gamepad className="w-6 h-6 text-neon-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-100">{server.displayName}</h1>
            <p className="text-sm text-gray-500">{server.name}</p>
          </div>
        </div>
        <span className={getStatusBadge(displayStatus)}>
          {displayStatus === 'running' && <span className="status-dot-online" />}
          {t(`serverStatus.${displayStatus}`)}
        </span>
      </div>

      {refreshError && (
        <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-amber-300">{t('serverDetails.refreshFailed')}</p>
            <p className="text-xs text-amber-400/80 mt-1">{refreshError}</p>
          </div>
          <button onClick={() => fetchServerDetails()} className="btn-secondary text-sm">
            <RefreshCw className="w-4 h-4" />
            {t('serverDetails.retryLoad')}
          </button>
        </div>
      )}

      {actionError && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-300">{t('serverDetails.operationRejected')}</p>
            <p className="text-sm text-red-400 mt-1">{actionErrorMessage}</p>
            {actionError.retryable && (
              <p className="text-xs text-amber-300 mt-2">{t('serverDetails.retryableError')}</p>
            )}
          </div>
          <button onClick={() => setActionError(null)} className="btn-ghost p-1">×</button>
        </div>
      )}

      {server.autoShutdown?.enabled && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-500/20 bg-amber-500/10 p-4">
          <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
          <div>
            <p className="text-sm font-medium text-amber-200">{t('serverDetails.autoShutdownTitle')}</p>
            <p className="mt-1 text-sm text-amber-300/80">
              {autoShutdownScheduledFor
                ? t('serverDetails.autoShutdownScheduled', {
                  time: autoShutdownScheduledFor.toLocaleString(i18n.language),
                  minutes: server.autoShutdown.idleMinutes,
                })
                : server.actualState === 'running'
                  ? t('serverDetails.autoShutdownWaiting', { minutes: server.autoShutdown.idleMinutes })
                  : t('serverDetails.autoShutdownEnabled', { minutes: server.autoShutdown.idleMinutes })}
            </p>
          </div>
        </div>
      )}

      {(server.actualState === 'error' || operationActive || ownerOperationFailed || server.desiredState === 'deleted') && (
        <div className={`mb-6 p-4 rounded-lg border ${server.actualState === 'error' || ownerOperationFailed
          ? 'bg-red-500/10 border-red-500/20'
          : 'bg-blue-500/10 border-blue-500/20'}`}>
          <div className="flex items-start gap-3">
            {server.actualState === 'error' || ownerOperationFailed
              ? <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              : <RefreshCw className="w-5 h-5 text-blue-400 shrink-0 animate-spin" />}
            <div>
              <p className="text-sm font-medium text-gray-200">
                {server.actualState === 'error' || ownerOperationFailed
                  ? t('serverDetails.serverError')
                  : t('serverDetails.operationInProgress', { operation: operationType || 'delete' })}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                {ownerErrorLabel
                  || (ownerOperationActive ? ownerPhaseLabel : null)
                  || server.lifecycleOperation?.lastError
                  || server.provisioning?.lastError
                  || server.stateReason
                  || t('serverDetails.waitForCompletion')}
              </p>
              {retryState?.nextRetryAt && (
                <p className="text-xs text-amber-300 mt-2">
                  {t('serverDetails.retryScheduled', { time: new Date(retryState.nextRetryAt).toLocaleTimeString() })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Startup Progress */}
          {['creating', 'starting'].includes(server.status) && (
            <StartupProgress
              progress={server.startupProgress}
              fallbackLabel={server.status === 'creating'
                ? t('serverDetails.provisioning')
                : t('serverDetails.starting')}
            />
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="stat-card">
              <div className="flex items-center justify-center mb-2">
                {healthOnline ? (
                  <Wifi className="w-5 h-5 text-neon-400" />
                ) : (
                  <WifiOff className="w-5 h-5 text-gray-500" />
                )}
              </div>
              <div className="stat-value">{healthOnline ? t('common.online') : t('common.offline')}</div>
              <div className="stat-label">{t('common.status')}</div>
            </div>
            
            <div className="stat-card">
              <div className="flex items-center justify-center mb-2">
                <Users className="w-5 h-5 text-cyber-400" />
              </div>
              <div className="stat-value">{monitoringStale ? '—' : server.monitoring.playerStats.currentPlayers}/{appliedMaxPlayers}</div>
              <div className="stat-label">
                {t('common.players')}
                {server.monitoring.playerStats.method ? ` · ${server.monitoring.playerStats.method}` : ''}
              </div>
            </div>
            
            <div className="stat-card">
              <div className="flex items-center justify-center mb-2">
                <HardDrive className="w-5 h-5 text-purple-400" />
              </div>
              <div className="stat-value">
                {liveResources
                  ? `${formatBytes(liveResources.memoryUsageBytes)} / ${formatBytes(liveResources.memoryLimitBytes)}`
                  : appliedMemory
                }
              </div>
              <div className="stat-label">
                {liveResources
                  ? `RAM (${liveResources.memoryPercent.toFixed(1)}%)`
                  : t('serverDetails.ramAllocated')
                }
              </div>
            </div>
            
            <div className="stat-card">
              <div className="flex items-center justify-center mb-2">
                <Cpu className="w-5 h-5 text-amber-400" />
              </div>
              <div className="stat-value">
                {liveResources
                  ? `${liveResources.cpuPercent.toFixed(1)}%`
                  : server.realTimeStats.estimatedPing
                }
              </div>
              <div className="stat-label">
                {liveResources ? 'CPU' : 'Ping'}
              </div>
            </div>
          </div>

          {/* Online Players */}
          {!monitoringStale && server.monitoring.playerStats.playersOnline.length > 0 && (
            <div className="card p-5">
              <h3 className="section-title mb-4">{t('serverDetails.onlinePlayers')}</h3>
              <div className="flex flex-wrap gap-2">
                {server.monitoring.playerStats.playersOnline.map((player) => (
                  <span key={player} className="badge-online">
                    {player}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Connection Info */}
          <div className="card p-5">
            <h3 className="section-title mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-gray-400" />
              {t('serverDetails.connection')}
            </h3>
            
            <div className="space-y-4">
              {/* Primary address: subdomain if DNS configured, otherwise IP:port */}
              {server.realTimeStats.dnsAddress ? (
                <>
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">{t('serverDetails.serverAddress')}</label>
                    <div className="flex items-center gap-2">
                      <code className="code flex-1">{server.realTimeStats.dnsAddress}</code>
                      <button
                        onClick={() => copyToClipboard(server.realTimeStats.dnsAddress!, 'mc')}
                        className="btn-ghost p-2"
                      >
                        {copied === 'mc' ? <Check className="w-4 h-4 text-neon-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {server.realTimeStats.directAddress && (
                    <div>
                      <label className="text-sm text-gray-400 mb-2 block">{t('serverDetails.directIp')}</label>
                      <div className="flex items-center gap-2">
                        <code className="code flex-1 text-xs">{server.realTimeStats.directAddress}</code>
                        <button
                          onClick={() => copyToClipboard(server.realTimeStats.directAddress!, 'ip')}
                          className="btn-ghost p-2"
                        >
                          {copied === 'ip' ? <Check className="w-4 h-4 text-neon-400" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">{t('serverDetails.serverAddress')}</label>
                  <div className="flex items-center gap-2">
                    <code className="code flex-1">
                      {server.realTimeStats.serverAddress || '—'}
                    </code>
                    <button
                      onClick={() => server.realTimeStats.serverAddress
                        && copyToClipboard(server.realTimeStats.serverAddress, 'mc')}
                      disabled={!server.realTimeStats.serverAddress}
                      className="btn-ghost p-2"
                    >
                      {copied === 'mc' ? <Check className="w-4 h-4 text-neon-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  {server.realTimeStats.serverAddress && (
                    <p className="text-xs text-gray-600 mt-1">
                      {t('serverDetails.dnsPending')}
                    </p>
                  )}
                </div>
              )}

              {server.sftp.available && (
                <>
                  <div className="divider" />
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">SFTP</label>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Host</span>
                        <div className="flex items-center gap-2">
                          <code className="code text-xs">{server.sftp.host}</code>
                          <button onClick={() => copyToClipboard(server.sftp.host, 'host')} className="btn-ghost p-1">
                            {copied === 'host' ? <Check className="w-3 h-3 text-neon-400" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Port</span>
                        <code className="code text-xs">{server.sftp.port}</code>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">User</span>
                        <code className="code text-xs">{server.sftp.username}</code>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Server Details */}
          <div className="card p-5">
            <h3 className="section-title mb-4">{t('serverDetails.details')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('serverDetails.package')}</span>
                <span className="text-gray-200">{appliedPackageName}</span>
              </div>
              {appliedSpec && (
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">{t('serverDetails.runtime')}</span>
                  <span
                    className="text-gray-300 font-mono text-xs truncate"
                    title={appliedSpec.runtime.contentHash}
                  >
                    {appliedSpec.runtime.runtimeId}@v{appliedSpec.runtime.version}
                  </span>
                </div>
              )}
              {appliedSpec && (
                <div className="flex justify-between gap-3 md:col-span-2">
                  <span className="text-gray-500">{t('serverDetails.specHash')}</span>
                  <code className="text-gray-400 text-xs break-all" title={appliedSpec.specHash}>
                    {appliedSpec.specHash}
                  </code>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">{t('serverDetails.memoryLimit')}</span>
                <span className="text-gray-200">{appliedMemory}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('serverDetails.cpuLimit')}</span>
                <span className="text-gray-200">{appliedCpu}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('serverDetails.storageLimit')}</span>
                <span className="text-gray-200">{appliedStorage}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('serverDetails.maxPlayersLimit')}</span>
                <span className="text-gray-200">{appliedMaxPlayers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('serverDetails.game')}</span>
                <span className="text-gray-200 capitalize">{server.game}</span>
              </div>
              {publicSettings.map(([name, value]) => (
                <div key={name} className="flex justify-between gap-3">
                  <span className="text-gray-500">{formatSettingLabel(name)}</span>
                  <span className="text-gray-200 text-right break-words">{formatSettingValue(value)}</span>
                </div>
              ))}
              <div className="flex justify-between">
                <span className="text-gray-500">{t('serverDetails.created')}</span>
                <span className="text-gray-200">{new Date(server.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('serverDetails.container')}</span>
                <span className="text-gray-400 font-mono text-xs">{server.containerName}</span>
              </div>
            </div>
          </div>

          {/* Activity Log */}
          <ActivityLog serverId={server._id} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <div className="card p-5">
            <h3 className="section-title mb-4">{t('serverDetails.actions')}</h3>
            <div className="space-y-2">
              {canStart && (
                <button
                  onClick={() => handleAction('start')}
                  disabled={actionLoading !== null}
                  className="btn-primary w-full"
                >
                  {actionLoading === 'start' ? (
                    <div className="spinner w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  {t('serverDetails.start')}
                </button>
              )}
              
              {canStop && (
                <button
                  onClick={() => handleAction('stop')}
                  disabled={actionLoading !== null}
                  className="btn-secondary w-full"
                >
                  {actionLoading === 'stop' ? (
                    <div className="spinner w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  {t('serverDetails.stop')}
                </button>
              )}
              
              {canRestart && (
                <button
                  onClick={() => handleAction('restart')}
                  disabled={actionLoading !== null}
                  className="btn-secondary w-full"
                >
                  {actionLoading === 'restart' ? (
                    <div className="spinner w-4 h-4" />
                  ) : (
                    <RotateCcw className="w-4 h-4" />
                  )}
                  {t('serverDetails.restart')}
                </button>
              )}

              {operationActive && (
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm text-blue-300 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {ownerOperationActive
                    ? ownerPhaseLabel
                    : t('serverDetails.operationPhase', {
                      operation: server.lifecycleOperation?.type,
                      phase: server.lifecycleOperation?.phase,
                    })}
                </div>
              )}

              {backupOperationActive && (
                <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-sm text-blue-300 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {t('backups.operationActive')} · {server.backupLock?.phase?.replace(/_/g, ' ')}
                </div>
              )}
              
              <button
                onClick={() => setShowFileManager(true)}
                className="btn-secondary w-full"
              >
                <FolderOpen className="w-4 h-4" />
                {t('serverDetails.files')}
              </button>
              
              <button
                onClick={() => setShowLogs(true)}
                className="btn-secondary w-full"
              >
                <ScrollText className="w-4 h-4" />
                {t('serverDetails.dockerLogs')}
              </button>

              {server.capabilities?.minecraftConsole && (
                <button
                  onClick={() => setShowMinecraftConsole(true)}
                  className="btn-secondary w-full"
                >
                  <Terminal className="w-4 h-4" />
                  {t('serverDetails.minecraftConsole')}
                </button>
              )}
              
              <div className="divider" />
              
              <button
                onClick={() => handleAction('delete')}
                disabled={actionLoading !== null || server.desiredState === 'deleted' || backupOperationActive}
                className="btn-danger w-full"
              >
                {actionLoading === 'delete' ? (
                  <div className="spinner w-4 h-4" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {t('serverDetails.delete')}
              </button>
            </div>
          </div>

          <BackupsPanel
            serverId={server._id}
          />

          {/* Host Info */}
          <div className="card p-5">
            <h3 className="section-title mb-4 flex items-center gap-2">
              <Cpu className="w-5 h-5 text-gray-400" />
              {t('serverDetails.host')}
              <span className={`ml-auto flex items-center gap-1 text-xs ${wsConnected ? 'text-green-500' : 'text-gray-500'}`}>
                {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                {wsConnected ? t('common.live') : t('common.offline')}
              </span>
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('serverDetails.agent')}</span>
                <span className="text-gray-200">{server.agentStatus.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('serverDetails.region')}</span>
                <span className="text-gray-200">{server.agentStatus.region}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('common.status')}</span>
                <span className={server.agentStatus.isConnected ? 'text-neon-400' : 'text-red-400'}>
                  {server.agentStatus.isConnected ? t('common.connected') : t('common.offline')}
                </span>
              </div>
              
              {server.agentStatus.systemMetrics && (
                <>
                  <div className="divider" />
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('serverDetails.containers')}</span>
                    <span className="text-gray-200">{server.agentStatus.systemMetrics.containers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">CPU</span>
                    <span className="text-gray-200">{server.agentStatus.systemMetrics.cpu}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">RAM</span>
                    <span className="text-gray-200">{server.agentStatus.systemMetrics.ram}</span>
                  </div>
                </>
              )}
              
              {server.monitoring.observedAt && (
                <div className="text-xs text-gray-600 pt-2">
                  Observed {new Date(server.monitoring.observedAt).toLocaleTimeString()}
                  {monitoringStale ? ' · stale' : ''}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* File Manager Modal */}
      {showFileManager && server && (
        <SecureFileManager
          serverId={server._id}
          serverName={server.displayName}
          onClose={() => setShowFileManager(false)}
        />
      )}

      {/* Logs Modal */}
      {showLogs && server && (
        <LogsModal
          serverId={server._id}
          serverName={server.displayName}
          onClose={() => setShowLogs(false)}
        />
      )}

      {showMinecraftConsole && server && (
        <MinecraftConsoleModal
          serverId={server._id}
          serverName={server.displayName}
          actualState={server.actualState}
          agentConnected={server.agentStatus.isConnected}
          supported={server.capabilities?.minecraftConsole === true}
          onClose={() => setShowMinecraftConsole(false)}
        />
      )}
    </div>
  );
}
